/**
 * Compass `FolderCoverageProvider` — five-layer fidelity model
 * (inventory / tags / cost / compliance / optimization) over the
 * Compass knowledge graph.
 *
 * See `docs/architecture/sources-as-folder-artifacts.md` for the
 * design rationale. Domain-agnostic types + registry live at
 * `src/core/folderCoverage/`; this file contributes the
 * Compass-specific layer logic.
 *
 * Pure function. No I/O — caller supplies the pre-fetched data.
 * Runs in the browser (folder dashboard) and the worker (future
 * coverage-trend summaries) identically.
 *
 * Ratios are resource-weighted: a layer is "filled" for a given
 * resource when the corresponding field is present on that resource.
 * We skip container resources (vpc / subnet / account / region) so a
 * 5-VPC scan with 2 VPCs untagged doesn't read as 40% coverage — the
 * question users want answered is "what fraction of my real
 * resources carry X," not "what fraction of graph nodes carry X."
 */

import type { PersistedNode } from '../../models/KnowledgeGraph';
import type {
  CoverageLayer,
  FolderCoverageInput,
  FolderCoverageProvider,
  FolderCoverageReport,
} from '../../../../core/folderCoverage/types';

const CONTAINER_TYPES = new Set([
  'aws_vpc',
  'aws_subnet',
  'aws_region',
  'aws_account',
  'vpc',
  'subnet',
  'region',
  'account',
]);

function isContainer(n: PersistedNode): boolean {
  const props = (n.props ?? {}) as Record<string, unknown>;
  const m =
    (props._manifestId as string | undefined) ??
    (props.manifestId as string | undefined) ??
    n.type;
  return CONTAINER_TYPES.has(String(m));
}

function hasTags(n: PersistedNode): boolean {
  const props = (n.props ?? {}) as Record<string, unknown>;
  const tags = props.tags;
  if (!tags || typeof tags !== 'object') return false;
  for (const k of Object.keys(tags as Record<string, unknown>)) {
    if (k && k.trim().length > 0) return true;
  }
  return false;
}

function hasCost(n: PersistedNode): boolean {
  const props = (n.props ?? {}) as Record<string, unknown>;
  const c = props.cost;
  if (typeof c === 'number' && Number.isFinite(c) && c > 0) return true;
  if (c && typeof c === 'object') {
    const m = (c as { monthly?: unknown }).monthly;
    return typeof m === 'number' && Number.isFinite(m);
  }
  return false;
}

function hasCompliance(n: PersistedNode): boolean {
  const props = (n.props ?? {}) as Record<string, unknown>;
  const list = props.configRuleList;
  if (Array.isArray(list) && list.length > 0) return true;
  const ct = props.complianceType;
  return typeof ct === 'string' && ct.length > 0;
}

function hasHostedLayer(input: FolderCoverageInput, layer: string): boolean {
  if (input.sources.some((source) => {
    if (source.kind !== 'aws-hosted-account') return false;
    const enabledLayers = (source.metadata as Record<string, unknown> | undefined)?.enabledLayers;
    return Array.isArray(enabledLayers) && enabledLayers.includes(layer);
  })) {
    return true;
  }

  return (input.runs ?? []).some((run) => {
    const metadata = run.metadata as Record<string, unknown> | undefined;
    const enabledLayers = metadata?.enabledLayers;
    if (Array.isArray(enabledLayers) && enabledLayers.includes(layer)) return true;
    const runLayers = metadata?.layers;
    return Array.isArray(runLayers) && runLayers.some((entry) => {
      if (!entry || typeof entry !== 'object') return false;
      return (entry as Record<string, unknown>).layer === layer;
    });
  });
}

function hasReviewSignals(nodes: readonly PersistedNode[], angle?: string): boolean {
  return nodes.some((node) => {
    const props = (node.props ?? {}) as Record<string, unknown>;
    const isSignal =
      props.source === 'aws-hosted-sync' ||
      (Array.isArray(node.tags) && node.tags.includes('review-signal'));
    if (!isSignal) return false;
    return !angle || props.angle === angle || node.tags?.includes(angle);
  });
}

function sourceLevelLayer(
  id: string,
  label: string,
  description: string,
  present: boolean,
  measurable: boolean,
  upsell: CoverageLayer['upsell'],
): CoverageLayer {
  const total = measurable ? 1 : 0;
  return {
    id,
    label,
    description,
    ratio: present ? 1 : 0,
    filled: present ? 1 : 0,
    total,
    upsell: present ? undefined : upsell,
  };
}

export function computeCompassFolderCoverage(input: FolderCoverageInput): FolderCoverageReport {
  const realResources = input.nodes.filter((n) => !isContainer(n));
  const total = realResources.length;

  const hasHostedInventory = hasHostedLayer(input, 'inventory');
  const hasHostedIam = hasHostedLayer(input, 'iam');
  const hasHostedCost = hasHostedLayer(input, 'cost');
  const hasHostedSecurityFindings =
    hasHostedLayer(input, 'security_findings') || hasReviewSignals(input.nodes, 'security');
  const hasHostedConfigCompliance =
    hasHostedLayer(input, 'config_compliance') || hasReviewSignals(input.nodes, 'compliance');
  const hasHostedOptimization =
    hasHostedLayer(input, 'optimization') || hasReviewSignals(input.nodes, 'optimization');

  const sourceSummary = {
    hasInventorySource: input.sources.some(
      (s) => s.kind === 'infra-scan' || s.kind === 'manual' || s.kind === 'graph-join' || s.kind === 'aws-hosted-account',
    ),
    hasCostSource: input.sources.some((s) => {
      const provider = (s.metadata as Record<string, unknown>)?.source;
      return (
        s.kind === 'aws-hosted-account' ||
        provider === 'aws-cost-explorer' ||
        provider === 'aws-cur' ||
        provider === 'azure-cost-management' ||
        provider === 'gcp-billing'
      );
    }) || hasHostedCost,
    hasConfigSource: input.sources.some((s) => {
      const meta = (s.metadata ?? {}) as Record<string, unknown>;
      return meta.format === 'aws-config' || s.kind === 'infra-scan';
    }) || hasHostedConfigCompliance,
    hasIamSource: hasHostedIam,
    hasSecurityFindingsSource: hasHostedSecurityFindings,
    hasOptimizationSource: hasHostedOptimization,
  };

  const tagsFilled = realResources.filter(hasTags).length;
  const costFilled = realResources.filter(hasCost).length;
  const complianceFilled = realResources.filter(hasCompliance).length;

  const layers: CoverageLayer[] = [
    {
      id: 'inventory',
      label: 'Inventory',
      description:
        'Core resources — VPC, EC2, RDS, IAM roles, and the rest of the topology.',
      ratio: total > 0 ? 1 : 0,
      filled: total,
      total,
      upsell:
        total === 0 && !hasHostedInventory
          ? {
              connectorId: 'aws-hosted-account',
              headline: 'No inventory yet',
              blurb:
                'Connect an AWS account for hosted inventory sync, or import a Terraform state / CFN / Config snapshot to populate the folder graph.',
            }
          : undefined,
    },
    {
      id: 'tags',
      label: 'Tags',
      description:
        'At least one tag on each resource — drives the Untagged finding, the Team / Env lenses, and the cost-by-owner breakdown.',
      ratio: total > 0 ? tagsFilled / total : 0,
      filled: tagsFilled,
      total,
      upsell:
        total > 0 && tagsFilled === 0
          ? {
              connectorId: 'aws-live-scan',
              headline: 'No tags captured',
              blurb:
                'Re-run the scanner with GetBucketTagging + ListRoleTags enabled so S3 and IAM resources carry their tag sets.',
            }
          : undefined,
    },
    {
      id: 'cost',
      label: 'Cost',
      description:
        'Monthly spend evidence. Hosted Cost Explorer starts as account/service/tag rollups; resource-level cost appears only when stable tags or resource keys can be joined.',
      ratio: total > 0 ? costFilled / total : 0,
      filled: costFilled,
      total,
      upsell:
        total > 0 && costFilled === 0
          ? {
              connectorId: 'aws-cost-explorer',
              headline: 'Connect AWS Cost Explorer',
              blurb:
                'Enable the hosted Cost Explorer layer for account/service/tag rollups. Resource-level cost heatmaps light up only where tags or resource keys make the join reliable.',
            }
          : undefined,
    },
    {
      id: 'compliance',
      label: 'Compliance',
      description:
        'AWS Config rule evaluations — every NON_COMPLIANT resource surfaces as a finding with severity mapped from the rule name.',
      ratio: total > 0 ? complianceFilled / total : 0,
      filled: complianceFilled,
      total,
      upsell:
        total > 0 && complianceFilled === 0
          ? {
              connectorId: 'aws-config-snapshot',
              headline: 'Connect AWS Config',
              blurb:
                'Upload a Config snapshot so SOC2 / PCI / CIS rule evaluations become first-class findings with severity mapped from rule names.',
            }
          : undefined,
    },
    sourceLevelLayer(
      'iam',
      'IAM',
      'Roles, trust relationships, and cross-account principals used by identity and attack-path review.',
      hasHostedIam,
      total > 0 || hasHostedIam,
      {
        connectorId: 'aws-hosted-account',
        headline: 'Enable IAM sync',
        blurb: 'Turn on the hosted IAM layer so role trust, principals, and identity edges are available in the review queue.',
      },
    ),
    sourceLevelLayer(
      'security_findings',
      'Security findings',
      'Provider findings and scanner-generated exposure signals that become review items.',
      hasHostedSecurityFindings,
      total > 0 || hasHostedSecurityFindings,
      {
        connectorId: 'aws-hosted-account',
        headline: 'Enable security findings',
        blurb: 'Enable Security Hub / scanner findings so runtime security evidence lands in the folder review queue.',
      },
    ),
    sourceLevelLayer(
      'optimization',
      'Optimization',
      'Advisor-style recommendations and utilization hints that feed waste and right-sizing reviews.',
      hasHostedOptimization,
      total > 0 || hasHostedOptimization,
      {
        connectorId: 'aws-hosted-account',
        headline: 'Enable optimization sync',
        blurb: 'Add the hosted optimization layer so waste, rightsizing, and cleanup candidates become reviewable signals.',
      },
    ),
  ];

  const enriched = layers.filter((l) => l.id !== 'inventory');
  const overall =
    enriched.length > 0
      ? enriched.reduce((a, l) => a + l.ratio, 0) / enriched.length
      : 0;

  return {
    domain: 'network',
    layers,
    overall,
    resourceCount: total,
    sourceSummary,
  };
}

/** Network diagram type also covers Compass folders. */
export const compassFolderCoverageProvider: FolderCoverageProvider = {
  domain: 'network',
  compute: computeCompassFolderCoverage,
};

/**
 * Back-compat alias — the original unit tests import
 * `computeFolderCoverage` by name. Returns the compass report
 * directly so callers that already know they're on a network
 * folder don't need to thread the domain.
 */
export function computeFolderCoverage(input: FolderCoverageInput): FolderCoverageReport {
  return computeCompassFolderCoverage(input);
}
